import { useApolloClient, useFragment } from '@apollo/client';
import classNames from 'classnames';
import * as inflection from 'inflection';
import * as _ from 'lodash-es';
import type { ComponentProps, DetailedHTMLProps, ReactNode } from 'react';
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import ExpandedPostModal from './modalView/ExpandedPostModal';
import PostDetailPreview from './PostDetailPreview';
import { PostEntityIcon } from './PostIcon';
import * as styles from './PostRow.module.scss';
import PostTitleWithIcons from './PostTitleWithIcons';
import { useAppDispatch } from '../app/redux/useAppSelector';
import Avatar from '../components/Avatar';
import AvatarStack from '../components/AvatarStack';
import Button from '../components/Button';
import HoverPreviewable from '../components/HoverPreviewable';
import OverflowMenu from '../components/OverflowMenu';
import SVGIcon from '../components/SVGIcon';
import PostSeriesMeta from './series/PostSeriesMeta';
import Timestamp from '../components/Timestamp';
import useLayoutSize, { LayoutSize } from './utils/useLayoutSize';
import type { DocumentType } from '../graphql/generated/gql';
import { gql } from '../graphql/generated/gql';
import { PostType } from '../graphql/generated/graphql';
import overflowMenuIcon from '../icons/non-prefilled/16x16/overflowMenu.svg';
import pinIcon from '../icons/prefilled/16x16/pinOutlineWithStroke.svg';
import useStayInViewport from '../topic/utils/useStayInViewport';
import { UIActions } from '../ui/uiSlice';
import UserDetailPreviewPopOver from '../user/UserDetailPreviewPopOver';
import Logger from '../utils/Logger';
import NestableLink from '../utils/NestableLink';
import Link from '../utils/router/Link';
import postPath from '../utils/url/postPath';

type MetaAttributeMap = {
  publishedAt: string;
  updatedAt: string;
  userViewedAt: string;
  insertedAt: string;
  popularity: number;
};

interface MetaProps<Attribute extends keyof MetaAttributeMap> {
  attribute: Attribute;
  values: Pick<MetaAttributeMap, Attribute>;
}

const Meta = <Attribute extends keyof MetaAttributeMap>(
  props: MetaProps<Attribute>,
) => {
  const layoutSize = useLayoutSize();

  const mappedValue = props.values[props.attribute];
  if (mappedValue == null) return null;

  switch (props.attribute) {
    case 'popularity': {
      if (typeof mappedValue === 'number') {
        return (
          <>
            {mappedValue} {inflection.inflect('view', mappedValue)}
          </>
        );
      }

      return null;
    }
    default: {
      let format: ComponentProps<typeof Timestamp>['format'] = 'default';
      if (layoutSize === LayoutSize.mobile) {
        format = 'minimal';
      } else if (layoutSize === LayoutSize.tablet) {
        format = 'short';
      }
      return <Timestamp format={format} date={mappedValue} />;
    }
  }
};

const POST_ROW_POST_FRAGMENT = gql(`
  fragment PostRow_post on Post {
    id
    type
    title
    ...PostEntityIcon_post
    ...postPath_post
  }
`);

const POST_ROW_ENHANCED_POST_FRAGMENT = gql(`
  fragment PostRowEnhancedPost on Post {
    id
    contributors {
      id
      ...AvatarStack_users
    }
    series {
      id
      ...PostSeriesMeta_series
    }
    ...PostTitleWithIcons_post
  }
`);

interface PostRowProps {
  post: DocumentType<typeof POST_ROW_POST_FRAGMENT>;
  meta?: React.ReactNode;
  pinned?: boolean;
  forcePreviewOpen?: boolean;
  seriesHoverCardEnabled?: boolean;
  renderOverflowMenu?: (close: () => void) => React.ReactNode;
}

const PostRow = ({
  post,
  meta,
  pinned = false,
  forcePreviewOpen,
  seriesHoverCardEnabled,
  renderOverflowMenu,
}: PostRowProps) => {
  const context = useContext(PostRowContext);

  const { data: enhancedPostData, complete: isComplete } = useFragment({
    fragment: POST_ROW_ENHANCED_POST_FRAGMENT,
    fragmentName: 'PostRowEnhancedPost',
    from: { __typename: 'Post', id: post.id },
  });

  const enhancedPost = isComplete ? enhancedPostData : undefined;

  const hasContext = !!context;
  useEffect(() => {
    if (!hasContext) {
      Logger.error('PostRow: No context found');
    }
  }, [hasContext]);

  const layoutSize = useLayoutSize();
  const [isOverflowMenuOpen, setIsOverflowMenuOpen] = useState(false);
  const isMobile = layoutSize === LayoutSize.mobile;

  const linkCommonProps = {
    to: postPath(post),
    onClick: (e) => {
      // Testing PostModalView by allowing meta+alt+click to open the modal
      if (e.metaKey && e.altKey) {
        e.preventDefault();
        dispatch(
          UIActions.openModal(ExpandedPostModal, { initialId: post.id }),
        );
      }
    },
  } as const satisfies Partial<
    ComponentProps<typeof NestableLink> & ComponentProps<typeof Link>
  >;

  const users = enhancedPost
    ? _.compact(enhancedPost.contributors).slice(0, isMobile ? 1 : 3)
    : [];

  const dispatch = useAppDispatch();

  const seriesMeta = enhancedPost?.series && (
    <header>
      <div className={styles.seriesHoverTarget}>
        <PostSeriesMeta
          postId={enhancedPost.id}
          series={enhancedPost.series}
          singleLine
          showHoverCard={seriesHoverCardEnabled}
        />
      </div>
    </header>
  );

  return (
    <div {...context?.getPropsForStayInViewport(post.id)}>
      <NestableLink
        {...linkCommonProps}
        className={styles.link}
        aria-label={`Post: ${post.title}`}
        role="article"
      >
        <HoverPreviewable
          renderPreview={({ isOpen }) => (
            <PostDetailPreview postId={post.id} open={isOpen} />
          )}
          forcePreviewOpen={forcePreviewOpen}
        >
          {(targetProps) => (
            <div
              className={classNames(styles.container, {
                [styles.mobile]: layoutSize === LayoutSize.mobile,
                [styles.tablet]: layoutSize === LayoutSize.tablet,
              })}
            >
              <div className={styles.iconContainer}>
                {pinned ? (
                  <>
                    <PostEntityIcon post={post} />
                    <div
                      className={classNames(styles.pinContainer, {
                        [styles.post]: post.type === PostType.Post,
                        [styles.template]: post.type === PostType.Template,
                      })}
                    >
                      <SVGIcon size="small">{pinIcon}</SVGIcon>
                    </div>
                  </>
                ) : (
                  <PostEntityIcon post={post} />
                )}
                {/* eslint-disable-next-line jsx-a11y/anchor-has-content */}
                <Link
                  {...linkCommonProps}
                  className={styles.hoverTarget}
                  aria-hidden
                  tabIndex={-1}
                  {...targetProps}
                />
              </div>
              <div className={styles.titleContainer}>
                {!!seriesMeta && (
                  <Link
                    {...linkCommonProps}
                    className={styles.seriesHoverTargetContainer}
                  >
                    {seriesMeta}
                  </Link>
                )}
                <div className={styles.title} role="heading" aria-level={5}>
                  <PostTitleWithIcons
                    truncate
                    {...(enhancedPost
                      ? { post: enhancedPost }
                      : { title: post.title })}
                  />
                </div>
              </div>
              <footer className={styles.contributors}>
                <AvatarStack items={users} noWrap size={isMobile ? 22 : 26}>
                  {(user, options) => (
                    <UserDetailPreviewPopOver
                      userId={user.id}
                      placement="top-start"
                    >
                      <Avatar user={user} size={options.size} />
                    </UserDetailPreviewPopOver>
                  )}
                </AvatarStack>
              </footer>
              <footer className={styles.meta}>{meta}</footer>
              {renderOverflowMenu && (
                <aside
                  className={classNames(styles.overflowMenu, {
                    [styles.isOpen]: isOverflowMenuOpen,
                  })}
                >
                  <OverflowMenu
                    preventCloseOnContentClick
                    button={
                      <Button
                        className={styles.overflowMenuButton}
                        aria-label="Actions"
                      >
                        <SVGIcon>{overflowMenuIcon}</SVGIcon>
                      </Button>
                    }
                    onOpenChange={setIsOverflowMenuOpen}
                    open={isOverflowMenuOpen}
                  >
                    <div className={styles.overflowMenuContent}>
                      {renderOverflowMenu(() => setIsOverflowMenuOpen(false))}
                    </div>
                  </OverflowMenu>
                </aside>
              )}
            </div>
          )}
        </HoverPreviewable>
      </NestableLink>
    </div>
  );
};

PostRow.Meta = Meta;

const PostRowContext = createContext<{
  getPropsForStayInViewport(
    id: string,
  ): DetailedHTMLProps<React.HTMLAttributes<HTMLDivElement>, HTMLDivElement>;
} | null>(null);

// eslint-disable-next-line slab-graphql/no-unused-fields
const POST_ROW_PROVIDER_POSTS_QUERY = gql(/** @complexity 8 */ `
  query PostRowProviderPosts($ids: [ID!]!) {
    posts(ids: $ids) {
      id
      ...PostRowEnhancedPost
    }
  }
`);

interface PostRowProviderProps {
  children: ReactNode;
}

/**
 * Provider to support progressive UI for post rows.
 *
 * Typical usage:
 * - Render rows with minimal props to support basic UI (e.g. `title` and fields that render the post icon).
 * - When a row becomes visible within the viewport, ask the provider to load richer data.
 * - Read richer data via `useFragment()` and upgrade the UI when available.
 */
const PostRowProvider = ({ children }: PostRowProviderProps) => {
  const client = useApolloClient();

  const loadPosts = useCallback(
    (ids: string[]) => {
      client
        .query({
          query: POST_ROW_PROVIDER_POSTS_QUERY,
          variables: { ids },
          fetchPolicy: 'network-only',
        })
        .catch(() => {
          // Ignore errors
        });
    },
    [client],
  );

  const getPropsForStayInViewport = useStayInViewport(loadPosts);

  const value = useMemo(
    () => ({ getPropsForStayInViewport }),
    [getPropsForStayInViewport],
  );

  return (
    <PostRowContext.Provider value={value}>{children}</PostRowContext.Provider>
  );
};

PostRow.Provider = PostRowProvider;

export default PostRow;
